// biome-ignore-all lint/correctness/useExhaustiveDependencies: historic

import useIsomorphicLayoutEffect from '@hooks/use-isomorphic-layout-effect';
import useOnce from '@hooks/use-once';
import { analytics } from '@modules/analytics';
import { inferBrowser } from '@modules/browser';
import { logger } from '@modules/logger';
import {
  Application,
  Assets,
  Container,
  Graphics,
  type PointData,
  Sprite,
  Text,
  type Texture,
  type Ticker,
} from 'pixi.js';
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';

import styles from './BlinqImageEditor.module.scss';
import { ZoomControls } from './Controls';
import {
  bgColor,
  canDragThreshold,
  defaultCanvasHeight,
  dragToRepositionEntryThreshold,
  editorVersion,
  fadeSpeed,
  highQualityImageGenerationRateMs,
  highQualityTextureResolution,
  lowQualityImageGenerationRateMs,
  positionSpringConstant,
  snaplineSensitivityPx,
  viewfinderLineWidth,
  zoomSpringConstant,
  zoomStep,
} from './constants';
import ImageEditorLoader from './EditorLoader';
import {
  animateSnapline,
  clampPosition,
  colorHexToNumber,
  createOnDragEnd,
  createOnDragMove,
  createOnDragStart,
  denormalizePosition,
  isPositionSnappable,
  makeViewfinder,
  normalizePosition,
  pointerDownHandler,
  pointerMoveHandler,
  pointerUpHandler,
  spring,
} from './helpers';
import type {
  DragSprite,
  EditorAction,
  EditorState,
  ImageEditorProps,
  ViewfinderAspectRatio,
  ZoomBounds,
} from './types';
import useColorSuggestions from './use-color-suggestions';

interface EditorPreset {
  viewfinderAspectRatio: ViewfinderAspectRatio;
  disableBgEdit: boolean;
  imgCover: boolean;
  suggestColors?: boolean;
}
const profilePreset: EditorPreset = {
  viewfinderAspectRatio: '1',
  disableBgEdit: true,
  imgCover: true,
};
const logoPreset: EditorPreset = {
  viewfinderAspectRatio: '16:9',
  disableBgEdit: false,
  imgCover: false,
  suggestColors: true,
};
const coverPreset: EditorPreset = {
  viewfinderAspectRatio: '16:9',
  disableBgEdit: true,
  imgCover: true,
};
// The QR-code logo sits in a square slot, so it crops 1:1 (matching the legacy
// editor); otherwise it behaves like the cover crop.
const qrPreset: EditorPreset = {
  viewfinderAspectRatio: '1',
  disableBgEdit: false,
  imgCover: false,
  suggestColors: true,
};

export const getEditorPreset = (name: 'profile' | 'logo' | 'cover' | 'qr'): EditorPreset => {
  switch (name) {
    case 'profile':
      return profilePreset;
    case 'logo':
      return logoPreset;
    case 'qr':
      return qrPreset;
    case 'cover':
    default:
      return coverPreset;
  }
};

/** Converts a string to a numeric aspect ratio. */
const toAspectRatio = (viewfinderAspectRatio: ViewfinderAspectRatio): number => {
  switch (viewfinderAspectRatio) {
    case '16:9':
      return 16 / 9;
    case '1':
    default:
      return 1;
  }
};

const initialState: EditorState = {
  editorVersion,
  zoom: undefined,
  position: { x: 0, y: 0 }, // start at the origin
  zoomBounds: {
    default: 1,
    defaultX: 1,
    defaultY: 1,
    max: 1,
    min: 0,
  },
  backgroundColor: bgColor,
};

const safeSetZoom = (zoom: number, bounds?: ZoomBounds | null): number => {
  if (!bounds) {
    logger.debug('[BlinqImageEditor] safeSetZoom: no bounds provided, returning zoom as-is', {
      zoom,
    });
    return zoom;
  }
  let result: number;
  let clampReason: string | null = null;
  if (zoom <= bounds.min) {
    result = bounds.min;
    clampReason = 'clamped to min';
  } else if (zoom >= bounds.max) {
    result = bounds.max;
    clampReason = 'clamped to max';
  } else {
    result = zoom;
  }
  logger.debug('[BlinqImageEditor] safeSetZoom', {
    inputZoom: zoom,
    bounds,
    clampReason,
    resultZoom: result,
  });
  return result;
};

function reducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case 'RESET':
      return action.state || { editorVersion };
    case 'LOADED':
      return { ...state, loaded: true, editorVersion };
    case 'SET_BG_COLOR':
      return {
        ...state,
        showCackgroundColorChangingMessage: action.showMessage,
        backgroundColor: action.color,
        editorVersion,
      };
    case 'SHOW_CUSTOM_BG_SELECTOR': {
      return { ...state, showCustomBackgroundColorSelector: action.value };
    }
    case 'COLOR_SUGGESTIONS':
      return {
        ...state,
        suggestedbackgroundColors: action.colors,
        editorVersion: editorVersion,
      };
    case 'SET_ZOOM': {
      const zb = action.bounds || state.zoomBounds;
      const newZoom = safeSetZoom(action.zoom, zb);
      logger.debug('[BlinqImageEditor] reducer SET_ZOOM', {
        'action.zoom': action.zoom,
        'action.bounds': action.bounds,
        'state.zoomBounds': state.zoomBounds,
        usedBounds: zb,
        resultZoom: newZoom,
      });
      return {
        ...state,
        zoom: newZoom,
        zoomBounds: zb,
        editorVersion: editorVersion,
      };
    }
    case 'SET_IMG_POSITION':
      return { ...state, position: action.position, editorVersion };
    default:
      throw new Error();
  }
}
export const useEditorReducer = (
  initial: EditorState | undefined = initialState,
): [EditorState, React.Dispatch<EditorAction>] => {
  return useReducer(reducer, initial);
};

const calculateViewfinderHeight = (app: Application): number => {
  return Math.floor(app.screen.height * 0.75);
};
const calculateViewfinderWidth = (
  viewfinderHeight: number,
  viewfinderAspectRatio: '16:9' | '1',
): number => {
  return Math.floor(viewfinderHeight * toAspectRatio(viewfinderAspectRatio));
};

const DOUBLE_TAP_THRESHOLD = 300; // amount of milliseconds allowable between two consecutive taps to be considered a double tap

const ImageEditor: React.FC<React.PropsWithChildren<ImageEditorProps>> = ({
  canvasHeight,
  disableBgEdit,
  focusOnMount,
  imgCover,
  imgSrc,
  onLowQualityChanged,
  onHighQualityChanged,
  editorState,
  updateEditorState,
  viewfinderAspectRatio,
  zoomCorrectionCoefficient,
}) => {
  // Log initial props on first render
  useEffect(() => {
    logger.debug('[BlinqImageEditor] ImageEditor mounted with props', {
      canvasHeight,
      imgCover,
      imgSrc: imgSrc?.substring(0, 100) + '...',
      viewfinderAspectRatio,
      zoomCorrectionCoefficient,
      editorState: {
        zoom: editorState.zoom,
        zoomBounds: editorState.zoomBounds,
        position: editorState.position,
        backgroundColor: editorState.backgroundColor,
      },
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const appRef = useRef<Application>(undefined); // keep a reference to PIXI app
  const [canReposition, setCanReposition] = useState(false); // used to show a message
  const [isSettingBackgroundColor, setIsSettingBackgroundColor] = useState(false); // used to show a message
  const imgSpritePositionRef = useRef<PointData>(undefined); // used to track changes in position
  const browser = useMemo(() => inferBrowser() || 'safari', []); // different browsers can have different settings
  const touchStartTimeRef = useRef<number | null>(null);

  // react twice to the setting of BG color, and then unset it later
  useEffect(() => {
    setIsSettingBackgroundColor(true);
  }, [editorState.backgroundColor, setIsSettingBackgroundColor]);
  useEffect(() => {
    if (isSettingBackgroundColor) {
      const timeout = setTimeout(() => setIsSettingBackgroundColor(false), 2000);
      return () => clearTimeout(timeout);
    }
  }, [isSettingBackgroundColor, setIsSettingBackgroundColor]);

  // the output data
  const lowQualityDataRef = useRef<string>(undefined); // used to keep track of LQ data in this editor context (outside React loop)

  const extractionRateGuage = useRef({
    lowQuality: 0,
    highQuality: 0,
    zooming: true,
    dragging: false,
  });

  const imageBackgroundColorSingleton = useRef({
    backgroundColor: editorState.backgroundColor ?? bgColor,
  });

  const imgSpriteRef = useRef<DragSprite>(undefined);
  const centerpointRef = useRef<PointData>(undefined);
  useEffect(() => {
    imageBackgroundColorSingleton.current.backgroundColor = editorState.backgroundColor ?? bgColor;
  }, [editorState]);

  const [resolution, setResolution] = useState<number | undefined>();
  useIsomorphicLayoutEffect(() => {
    setResolution(window.devicePixelRatio);
  });

  const [editorAspectRatio, setEditorAspectRatio] = useState<number | undefined>(undefined);
  useIsomorphicLayoutEffect(() => {
    setEditorAspectRatio(toAspectRatio(viewfinderAspectRatio));
  }, [viewfinderAspectRatio]);

  // remembers the state of the editor since the last HQ render.
  const highQualityRenderEditorState = useRef<{
    spritePosition?: PointData;
    backgroundColor?: number;
    zoom?: number;
  }>({});

  const canvasDimensions = {
    y: canvasHeight || defaultCanvasHeight,
    x: (16 / 9) * (canvasHeight || defaultCanvasHeight), // canvas should always be 16:9
  };

  // load the app and canvas
  useEffect(() => {
    if (
      canvasRef.current &&
      canvasContainerRef.current &&
      editorAspectRatio &&
      resolution &&
      !appRef.current
    ) {
      analytics.track({ name: 'editor_initialized', properties: { module: 'image_editor' } });

      // initialise PIXI asynchronously (Pixi v8 requirement)
      const app = new Application();

      const initApp = async () => {
        if (!canvasRef.current || !canvasContainerRef.current) return;

        await app.init({
          height: canvasDimensions.y,
          width: canvasDimensions.x, // canvas should always be 16:9
          antialias: true,
          canvas: canvasRef.current,
          resolution: resolution,
          resizeTo: canvasContainerRef.current,
          backgroundAlpha: 0, // start with the background alpha as zero, to fade in
          backgroundColor: bgColor,
        });

        appRef.current = app;
        if (focusOnMount && canvasRef.current) {
          canvasRef.current.focus();
        }

        const dimensions = { x: app.screen.width, y: app.screen.height };
        const backgroundDimensions = { x: app.screen.width + 2, y: app.screen.height + 2 };
        centerpointRef.current = { x: dimensions.x / 2, y: dimensions.y / 2 };
        const centerpoint = centerpointRef.current;

        // Load texture using Assets (Pixi v8 requirement)
        let texture: Texture;
        try {
          if (imgSrc.startsWith('blob:') || imgSrc.startsWith('data:')) {
            // Blob URLs and data URLs need explicit loadParser hint
            // (they don't have file extensions for Pixi.js v8 to infer type)
            texture = await Assets.load<Texture>({
              src: imgSrc,
              loadParser: 'loadTextures',
            });
          } else {
            texture = await Assets.load<Texture>(imgSrc);
          }
        } catch (error) {
          logger.error('[BlinqImageEditor] Failed to load texture', {
            error,
            imgSrc: imgSrc.substring(0, 100),
          });
          return;
        }

        if (!texture) {
          logger.error('[BlinqImageEditor] Assets.load returned null', {
            imgSrc: imgSrc.substring(0, 100),
          });
          return;
        }

        const viewfinderHeight = calculateViewfinderHeight(app);
        const viewfinderWidth = calculateViewfinderWidth(viewfinderHeight, viewfinderAspectRatio);
        const imageContainer = new Container();
        imageContainer.label = 'imageContainer';
        imgSpriteRef.current = new Sprite(texture) as DragSprite;

        const imgSprite = imgSpriteRef.current;
        if (!imgSprite) return;

        imgSprite.label = 'imgSprite';
        imgSprite.anchor.set(0.5); // sets the anchor in the middle of the parent
        if (editorState.position) {
          const denormalizedPosition = denormalizePosition(editorState.position, dimensions);
          imgSprite.position.set(denormalizedPosition.x, denormalizedPosition.y);
        } else {
          imgSprite.position.set(centerpoint.x, centerpoint.y);
        }
        imgSprite.eventMode = 'static';
        imgSprite.cursor = 'grab';
        imageContainer.width = app.screen.width;
        imageContainer.height = app.screen.height;

        app.stage.addChild(imageContainer);

        // Pixi v8 Graphics API: build shape then fill
        const imageBackground = new Graphics()
          .rect(0, 0, viewfinderWidth, viewfinderHeight)
          .stroke({ width: viewfinderLineWidth, color: 0xffffff, alpha: 0.1 })
          .fill(imageBackgroundColorSingleton.current.backgroundColor || 0xffffff);

        imageBackground.label = 'imageBackground';
        imageBackground.width = backgroundDimensions.x;
        imageBackground.height = backgroundDimensions.y;
        imageContainer.addChild(imageBackground);
        imageContainer.addChild(imgSprite);

        // controls container
        const controlsContainer = new Container();
        controlsContainer.label = 'controlsContainer';

        controlsContainer.width = app.screen.width;
        controlsContainer.height = app.screen.height;
        app.stage.addChild(controlsContainer);

        const viewfinder = makeViewfinder({
          lineWidth: viewfinderLineWidth,
          height: viewfinderHeight,
          width: viewfinderWidth,
          editorDimensions: {
            x: app.screen.width,
            y: app.screen.height,
          },
        });
        // put the background at the same position as the viewfinder.
        // Note: background must stay at (0,0) because the extract() function calculates
        // the crop frame relative to imageContainerBounds, which would break if we move it.
        imageBackground.position.set(0, 0);
        // calculate min and max position for center of sprite

        const calcMinImgPosition = () => {
          const min = { x: viewfinder.rectangle.x, y: viewfinder.rectangle.y };
          if (imgCover) {
            // opposite bound - half img size
            min.x += viewfinder.rectangle.width - imgSprite.width / 2;
            min.y += viewfinder.rectangle.height - imgSprite.height / 2;
          } else {
            if (imgSprite.width >= viewfinder.rectangle.width) {
              min.x += viewfinder.rectangle.width - imgSprite.width / 2;
            } else {
              min.x += imgSprite.width / 2;
            }
            if (imgSprite.height >= viewfinder.rectangle.height) {
              min.y += viewfinder.rectangle.height - imgSprite.height / 2;
            } else {
              min.y += imgSprite.height / 2;
            }
          }
          return min;
        };
        const calcMaxImgPosition = () => {
          const max = {
            x: viewfinder.rectangle.x + viewfinder.rectangle.width,
            y: viewfinder.rectangle.y + viewfinder.rectangle.height,
          };

          if (imgCover) {
            max.x -= viewfinder.rectangle.width - imgSprite.width / 2;
            max.y -= viewfinder.rectangle.height - imgSprite.height / 2;
          } else {
            if (imgSprite.width >= viewfinder.rectangle.width) {
              max.x -= viewfinder.rectangle.width - imgSprite.width / 2;
            } else {
              max.x -= imgSprite.width / 2;
            }
            if (imgSprite.height >= viewfinder.rectangle.height) {
              max.y -= viewfinder.rectangle.height - imgSprite.height / 2;
            } else {
              max.y -= imgSprite.height / 2;
            }
          }
          return max;
        };

        /** Calculates the difference between the img width and viewfinder width */
        const imgViewfinderXDelta = (): number => {
          return imgSprite.width - viewfinder.rectangle.width;
        };
        /** Calculates the difference between the img height and viewfinder height */
        const imgViewfinderYDelta = (): number => {
          return imgSprite.height - viewfinder.rectangle.height;
        };
        /** Calculates the max delta between the img width and viewfinder width, or image height and viewfinder height */
        const maxImgViewfinderDelta = (): number => {
          return Math.max(imgViewfinderXDelta(), imgViewfinderYDelta());
        };

        // functions to tell whether we can drag in X or Y directions.
        const canDragX = (): boolean => {
          return imgViewfinderXDelta() > canDragThreshold;
        };
        const canDragY = (): boolean => {
          return imgViewfinderYDelta() > canDragThreshold;
        };
        const canDrag = (): boolean => {
          return canDragX() || canDragY();
        };
        //     // drag handlers
        imgSprite.on('pointerdown', createOnDragStart(imgSprite, canDrag));
        imgSprite.on(
          'pointerup',
          createOnDragEnd(imgSprite, app, () => {
            extractionRateGuage.current.dragging = false;
          }),
        );
        imgSprite.on(
          'pointerupoutside',
          createOnDragEnd(imgSprite, app, () => {
            extractionRateGuage.current.dragging = false;
          }),
        );
        imgSprite.on(
          'pointermove',
          createOnDragMove(
            imgSprite,
            // biome-ignore lint/suspicious/noAssignInExpressions: historic
            () => (extractionRateGuage.current.dragging = true),
            calcMinImgPosition,
            calcMaxImgPosition,
          ),
        );
        controlsContainer.addChild(viewfinder.rectangle);
        controlsContainer.addChild(viewfinder.blurrer);

        const handleInitialiseZoom = () => {
          // when the image actually loads
          // recalculate zoom bounds
          const widthRatio = viewfinder.rectangle.width / imgSprite.width;
          const heightRatio = viewfinder.rectangle.height / imgSprite.height;
          const defaultZoom = imgCover
            ? Math.max(heightRatio, widthRatio)
            : Math.min(heightRatio, widthRatio);
          // const initialZoom =
          //   editorState.zoom === null || editorState.zoom === undefined ? defaultZoom : editorState.zoom;

          const zoomBounds = {
            min: imgCover ? defaultZoom : Math.min(heightRatio, widthRatio) / 2, // allow zoom out if not cover
            max: defaultZoom * 3,
            default: defaultZoom,
            defaultX: widthRatio,
            defaultY: heightRatio,
          };

          const initialZoom =
            editorState.zoom === null ||
            editorState.zoom === undefined ||
            editorState.zoom > zoomBounds.max || // NEW: Check if exceeds max
            editorState.zoom < zoomBounds.min // NEW: Check if below min
              ? defaultZoom
              : editorState.zoom;
          const correctedZoom = zoomCorrectionCoefficient
            ? initialZoom * zoomCorrectionCoefficient
            : initialZoom;

          logger.debug('[BlinqImageEditor] handleInitialiseZoom called', {
            viewfinderDimensions: {
              width: viewfinder.rectangle.width,
              height: viewfinder.rectangle.height,
            },
            imgSpriteDimensions: {
              width: imgSprite.width,
              height: imgSprite.height,
            },
            widthRatio,
            heightRatio,
            imgCover,
            defaultZoom,
            editorStateZoom: editorState.zoom,
            initialZoom,
            zoomCorrectionCoefficient,
            correctedZoom,
            zoomBounds,
          });

          imgSprite.scale.set(initialZoom || undefined); // skip animation initially.

          zoomSingleton.current.default = defaultZoom;
          zoomSingleton.current.initial = initialZoom;
          zoomSingleton.current.defaultX = widthRatio;
          zoomSingleton.current.defaultY = heightRatio;
          setZoom(correctedZoom, zoomBounds); //no need for safeset here
        };
        //     // drag to reposition text and cursor animation
        app.ticker.add(() => {
          if (canDrag()) {
            imgSprite.cursor = imgSprite.dragging === 2 ? 'grabbing' : 'grab';
            // only show "Drag to Reposition" if the dragging is significant
            setCanReposition(maxImgViewfinderDelta() > dragToRepositionEntryThreshold);
          } else {
            setCanReposition(false);
            imgSprite.cursor = 'default';
          }
        });

        // Since texture is already loaded via Assets.load, we can initialize zoom immediately
        if (imgSprite.height > 1) {
          handleInitialiseZoom();
        }

        const ctrlInfo = new Text({
          text: 'blank',
          style: {
            fontFamily: 'Arial',
            fontSize: 24,
            fill: 0xffffff,
            align: 'center',
          },
        });
        ctrlInfo.label = 'ctrlInfo';

        // uncomment this line to print debug info on the canvas
        // controlsContainer.addChild(ctrlInfo);

        // run the zoom animation - Pixi v8: ticker callback receives Ticker object
        app.ticker.add((ticker: Ticker) => {
          const delta = ticker.deltaTime;
          const desiredZoom = zoomSingleton.current.zoom;
          // img sprite is anchored to the center of the renderer
          // here we should set the zoom to be fractionally closer to the desired zoom z
          // spring function
          const currentZoom = imgSprite.scale.x; // x = y
          const zoomDelta = currentZoom - desiredZoom;

          // update the debug info
          ctrlInfo.text = `z=${currentZoom.toFixed(2)}, spritePos={x:${imgSprite.position.x.toFixed(
            2,
          )}, y: ${imgSprite.position.y.toFixed(2)}  `;

          // make this function to for either control path.
          const runClamping = () => {
            // when we zoom, we should also ensure the position of the sprite is clamped.
            const minPos = calcMinImgPosition();
            const maxPos = calcMaxImgPosition();
            const clamped = clampPosition(minPos, maxPos, imgSprite.position);
            imgSprite.position.set(clamped.x, clamped.y);
          };

          if (Math.abs(zoomDelta) < zoomStep) {
            //  if the delta is small, just snap
            imgSprite.scale.set(desiredZoom);
            extractionRateGuage.current.zooming = false;
            runClamping();
            return; // exit early.
          } else {
            imgSprite.scale.set(spring(zoomSpringConstant * delta, currentZoom, desiredZoom));
            extractionRateGuage.current.zooming = true;
          }

          // zoom animation has a side effect on the position.
          // when zooming out, we want the sprite to move towards the center.
          // when the zoom is at the starting zoom (i.e. the image fits neatly with width or height)
          // then the image should be centered.
          const distanceFromDefaultXZoom = currentZoom - zoomSingleton.current.defaultX; // +ve if zoomed in
          const distanceFromDefaultYZoom = currentZoom - zoomSingleton.current.defaultY; // +ve if zoomed in

          // we're zoomed out - lock the image to the center in 1 or more dimensions
          if (distanceFromDefaultXZoom < 0) {
            // then lock teh X
            imgSprite.position.x = centerpoint.x;
          }
          if (distanceFromDefaultYZoom < 0) {
            // then lock the Y
            imgSprite.position.y = centerpoint.y;
          }
          runClamping();
        });

        const snaplineX = new Graphics()
          .moveTo(centerpoint.x, 0)
          .lineTo(centerpoint.x, app.screen.height)
          .stroke({ width: 1, color: 0xffffff });
        snaplineX.alpha = 0;
        controlsContainer.addChild(snaplineX);

        const snaplineY = new Graphics()
          .moveTo(0, centerpoint.y)
          .lineTo(app.screen.width, centerpoint.y)
          .stroke({ width: 1, color: 0xffffff });
        snaplineY.alpha = 0;
        controlsContainer.addChild(snaplineY);

        // snap animation - Pixi v8: ticker callback receives Ticker object
        app.ticker.add((ticker: Ticker) => {
          const delta = ticker.deltaTime;
          // we only want to animate the snapine if we are dragging the image.
          if (
            animateSnapline(
              snaplineX,
              canDragX,
              !!imgSprite.dragging,
              centerpoint.x,
              imgSprite.position.x,
              delta,
            )
          ) {
            // snap X is on - therefore set the imgSprite position to the snaplineX position (actually do the snap)
            imgSprite.position.x = centerpoint.x;
          }
          if (
            animateSnapline(
              snaplineY,
              canDragY,
              !!imgSprite.dragging,
              centerpoint.y,
              imgSprite.position.y,
              delta,
            )
          ) {
            // do the snap
            imgSprite.position.y = centerpoint.y;
          }
        });

        // add a data extract loop - allows preview
        app.ticker.add(() => {
          extractionRateGuage.current.lowQuality += app.ticker.deltaMS;
          if (extractionRateGuage.current.lowQuality > lowQualityImageGenerationRateMs) {
            extractionRateGuage.current.lowQuality = 0;
            extract(app, 'low');
          }
        });
        app.ticker.add(() => {
          if (extractionRateGuage.current.dragging || extractionRateGuage.current.zooming) {
            extractionRateGuage.current.highQuality = 0; // don't extract tick up high quality if moving
          }
          extractionRateGuage.current.highQuality += app.ticker.deltaMS;
          if (
            extractionRateGuage.current.highQuality > highQualityImageGenerationRateMs[browser] &&
            !extractionRateGuage.current.zooming &&
            !extractionRateGuage.current.dragging // don't extract tick up high quality if moving
          ) {
            extractionRateGuage.current.highQuality = 0;
            extract(app, 'high');
          }
        });
        // add position mover animation - Pixi v8: ticker callback receives Ticker object
        app.ticker.add((ticker: Ticker) => {
          const delta = ticker.deltaTime;
          if (positionSetterRef.current) {
            // check if we need to reset the thing
            let newX = positionSetterRef.current.x;
            let newY = positionSetterRef.current.y;
            if (Math.abs(imgSprite.position.x - positionSetterRef.current.x) > 0.1) {
              // then spring
              newX = spring(
                delta * positionSpringConstant,
                imgSprite.position.x,
                positionSetterRef.current.x,
              );
            }
            if (Math.abs(imgSprite.position.y - positionSetterRef.current.y) > 0.1) {
              newY = spring(
                delta * positionSpringConstant,
                imgSprite.position.y,
                positionSetterRef.current.y,
              );
            }
            imgSprite.position.set(newX, newY);
            if (
              imgSprite.position.x === positionSetterRef.current.x &&
              imgSprite.position.y === positionSetterRef.current.y
            ) {
              positionSetterRef.current = undefined;
            }
          }
        });

        // colour buttons
        const setBgColor = (c: number) => {
          const old = imageContainer.getChildByLabel('imageBackground');
          const imageBackground = new Graphics()
            .rect(0, 0, backgroundDimensions.x, backgroundDimensions.y)
            .stroke({ width: 1, color: 0xffffff })
            .fill({ color: c, alpha: 1 });
          // Note: background must stay at (0,0) because the extract() function calculates
          // the crop frame relative to imageContainerBounds, which would break if we move it.
          imageBackground.position.set(0, 0);
          imageBackground.label = 'imageBackground';
          imageContainer.addChildAt(imageBackground, 1);
          if (old) {
            imageContainer.removeChild(old);
          }
        };

        // background color animation
        app.ticker.add(() => {
          const old = imageContainer.getChildByLabel('imageBackground') as Graphics | null;
          if (old && imageBackgroundColorSingleton.current.backgroundColor !== undefined) {
            // Pixi v8: Graphics no longer has .fill.color, need to redraw
            setBgColor(imageBackgroundColorSingleton.current.backgroundColor || 0);
          }
        });

        // container for fader
        // the fader is there at the start to hide everything as we fade in
        const faderContainer = new Container();
        const fader = new Graphics()
          .rect(0, 0, app.renderer.height, app.renderer.width)
          .fill(colorHexToNumber('0xFFFFFF')); // white
        faderContainer.addChild(fader);
        fader.label = 'fader';
        faderContainer.label = 'faderContainer';
        app.stage.addChild(faderContainer);

        // fade-in animation - Pixi v8: ticker callback receives Ticker object
        app.ticker.add((ticker: Ticker) => {
          const delta = ticker.deltaTime;
          const faderContainerRef = app.stage.getChildByLabel('faderContainer');
          if (!faderContainerRef) {
            return;
          }
          if (fader.alpha === 0) {
            const faderRef = faderContainer.getChildByLabel('fader');
            if (faderRef) {
              faderContainer.removeChild(faderRef);
            }

            app.stage.removeChild(faderContainerRef);
          } else {
            // ensure the fa
            fader.width = app.screen.width;
            fader.height = app.screen.height;
            // fader alpha drops
            // const nextAlpha = spring(constants.fadeSpeed * delta, fader.alpha, 0);
            const nextAlpha = fader.alpha - delta * fadeSpeed;
            if (nextAlpha < 0.005) {
              fader.alpha = 0; // clamp to zero
            } else {
              fader.alpha = nextAlpha;
            }
            // renderer alpha goes up inversely

            app.renderer.background.alpha = Math.max(1 - fader.alpha, 0);
          }
        });
        // add a loop that updates editor positon when sprite moves
        app.ticker.add(() => {
          const newPosition = { x: imgSprite.position.x, y: imgSprite.position.y };
          imgSpritePositionRef.current ??= newPosition; // init if null
          const oldPosition = imgSpritePositionRef.current;
          if (oldPosition.x !== newPosition.x || oldPosition.y !== newPosition.y) {
            // then update
            const normalized = normalizePosition(newPosition, dimensions);
            updateEditorState({ type: 'SET_IMG_POSITION', position: normalized });
          }
        });

        updateEditorState({ type: 'LOADED' });
      };

      initApp();

      return () => {
        if (app) {
          app.destroy();
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorAspectRatio, resolution]);

  // this runs during the loop.
  // i.e. on every interaction, and once at the start of the load.

  const extract = async (app: Application, quality: 'high' | 'low' = 'low') => {
    if (app.stage) {
      const start = Date.now();
      const imageContainer = app.stage.getChildByLabel('imageContainer');
      const controlsContainer = app.stage.getChildByLabel('controlsContainer');
      if (controlsContainer && imageContainer) {
        const viewfinder = (controlsContainer as Container).getChildByLabel('viewfinder');
        if (!viewfinder) {
          return;
        }

        const imgContainer = imageContainer as Container;
        const imgSprite = imgContainer.getChildByLabel('imgSprite');

        // Pixi v8: generateTexture's frame parameter doesn't work the same as v7's extract.canvas(texture, frame).
        // Instead, we generate the full texture and manually crop using Canvas API.
        //
        // IMPORTANT: generateTexture() without a frame generates a texture sized to the bounding box
        // of the visible content in the container. When zoomed in, the sprite extends beyond the screen,
        // so the texture is larger than the screen. The texture's (0,0) corresponds to
        // imageContainer.getBounds().x/y, NOT screen (0,0).
        //
        // We must offset our crop coordinates by the container bounds to account for this.
        const containerBounds = (imageContainer as Container).getBounds();

        const viewfinderX = viewfinder.x + 1;
        const viewfinderY = viewfinder.y + 1;
        const viewfinderWidth = (viewfinder as Graphics).width - 3 * viewfinderLineWidth;
        const viewfinderHeight = (viewfinder as Graphics).height - 3 * viewfinderLineWidth;

        // Calculate crop coordinates relative to the texture's origin (which is containerBounds.x/y)
        const cropX = viewfinderX - containerBounds.x;
        const cropY = viewfinderY - containerBounds.y;

        if (quality == 'high') {
          // check if we actually need to do a render
          const imageBackground = imgContainer.getChildByLabel('imageBackground');
          if (!imgSprite || !imageBackground) {
            return;
          }

          if (
            imgSprite.position.x !== highQualityRenderEditorState.current.spritePosition?.x ||
            imgSprite.position.y !== highQualityRenderEditorState.current.spritePosition?.y ||
            zoomSingleton.current.zoom !== highQualityRenderEditorState.current.zoom
          ) {
            // then we need to rerender. save the state for next time.
            highQualityRenderEditorState.current.spritePosition = {
              x: imgSprite.position.x,
              y: imgSprite.position.y,
            };
            highQualityRenderEditorState.current.zoom = zoomSingleton.current.zoom;

            const targetResolution = highQualityTextureResolution[browser];

            // Generate full texture of the imageContainer
            const texture = app.renderer.generateTexture({
              target: imageContainer,
              resolution: targetResolution,
            });

            const fullCanvas = app.renderer.extract.canvas(texture) as HTMLCanvasElement;

            // Manually crop using Canvas API - scale coordinates by resolution
            const cropCanvas = document.createElement('canvas');
            cropCanvas.width = viewfinderWidth * targetResolution;
            cropCanvas.height = viewfinderHeight * targetResolution;
            const ctx = cropCanvas.getContext('2d');
            if (ctx) {
              ctx.drawImage(
                fullCanvas,
                cropX * targetResolution,
                cropY * targetResolution,
                viewfinderWidth * targetResolution,
                viewfinderHeight * targetResolution,
                0,
                0,
                cropCanvas.width,
                cropCanvas.height,
              );
            }

            const newData = cropCanvas.toDataURL();
            onHighQualityChanged?.(newData);
            texture.destroy(true);

            const t = Date.now() - start;
            if (t > 75) logger.warn('[BlinqImageEditor] HQ extract took long', { durationMs: t });
          }
        } else {
          const lowResolution = 0.5;

          // Generate full texture of the imageContainer
          const texture = app.renderer.generateTexture({
            target: imageContainer,
            resolution: lowResolution,
          });

          const fullCanvas = app.renderer.extract.canvas(texture) as HTMLCanvasElement;

          // Manually crop using Canvas API - scale coordinates by resolution
          const cropCanvas = document.createElement('canvas');
          cropCanvas.width = viewfinderWidth * lowResolution;
          cropCanvas.height = viewfinderHeight * lowResolution;
          const ctx = cropCanvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(
              fullCanvas,
              cropX * lowResolution,
              cropY * lowResolution,
              viewfinderWidth * lowResolution,
              viewfinderHeight * lowResolution,
              0,
              0,
              cropCanvas.width,
              cropCanvas.height,
            );
          }

          const newData = cropCanvas.toDataURL();
          if (lowQualityDataRef.current !== newData) {
            lowQualityDataRef.current = newData; // update the working reference for next loop comparison
            onHighQualityChanged?.(undefined); // mark the high quality data as expired
            onLowQualityChanged?.(newData); // update the new low quality data for external consumers
            highQualityRenderEditorState.current = {}; // ensure high quality re-renders after this
          }
          texture.destroy(true);
        }
      }
    }
  };

  const hasZoomedForAnalyics = useRef(false);
  const trackZoomForAnalytics = () => {
    if (!hasZoomedForAnalyics.current) {
      analytics.track({ name: 'editor_zoomed', properties: { module: 'image_editor' } });
      hasZoomedForAnalyics.current = true;
    }
  };

  const setZoom = useCallback(
    (zoom: number, bounds?: ZoomBounds) =>
      updateEditorState({ type: 'SET_ZOOM', zoom: zoom, bounds }),
    [updateEditorState],
  );

  const positionSetterRef = useRef<PointData>(undefined);
  const zoomSingleton = useRef({
    zoom: 1,
    default: 1,
    initial: 1,
    defaultX: 1,
    defaultY: 1,
  });

  useEffect(() => {
    if (zoomSingleton.current.zoom !== editorState.zoom && editorState.zoom !== undefined) {
      zoomSingleton.current.zoom = editorState.zoom || 1;
    }
  }, [editorState]);

  const handleMouseWheel = useMemo(
    () => (ev: WheelEvent) => {
      if (editorState.zoom) {
        const nextZoom = editorState.zoom - ev.deltaY / 500; // magic number for smooth scrolling
        setZoom(nextZoom);
        ev.preventDefault();
        trackZoomForAnalytics();
      }
    },
    [editorState, setZoom],
  );

  // add a handler for the wheel to zoom
  useEffect(() => {
    const current = canvasRef.current;
    current?.addEventListener('wheel', handleMouseWheel, { passive: false });
    return () => current?.removeEventListener('wheel', handleMouseWheel);
  }, [handleMouseWheel]);

  // Prevent other elements being selected when dragging from the canvas
  useEffect(() => {
    if (canvasRef.current) {
      canvasRef.current.onselectstart = function () {
        return false;
      };
    }
  });

  // Pinch to zoom gestures (phone/ tablet)
  const pointerState = useRef({ evCache: [], prevDiff: -1 });
  const moveHandler = useMemo(
    () => (ev: PointerEvent) => {
      if (editorState.zoom !== undefined) {
        pointerMoveHandler(ev, pointerState.current, editorState.zoom || 1, setZoom);
        trackZoomForAnalytics();
      }
    },
    [editorState, setZoom],
  );

  const handleCanvasKeyDown = (ev: React.KeyboardEvent) => {
    if (imgSpriteRef.current && centerpointRef.current) {
      const imgSprite = imgSpriteRef.current;
      // move more when near a snapline, so we don't get pulled back to the snap point
      // move more when the key is help down
      const yDelta =
        isPositionSnappable(imgSprite.position.y, centerpointRef.current.y) || ev.repeat
          ? snaplineSensitivityPx
          : 1;
      const xDelta =
        isPositionSnappable(imgSprite.position.x, centerpointRef.current.x) || ev.repeat
          ? snaplineSensitivityPx
          : 1;
      switch (ev.key) {
        case 'ArrowUp':
          imgSprite.position.set(imgSprite.position.x, imgSprite.position.y - yDelta);
          ev.preventDefault();
          break;
        case 'ArrowRight':
          imgSprite.position.set(imgSprite.position.x + xDelta, imgSprite.position.y);
          ev.preventDefault();
          break;
        case 'ArrowDown':
          imgSprite.position.set(imgSprite.position.x, imgSprite.position.y + yDelta);
          ev.preventDefault();
          break;
        case 'ArrowLeft':
          imgSprite.position.set(imgSprite.position.x - xDelta, imgSprite.position.y);
          ev.preventDefault();
          break;
        case 'Home':
        case 'End':
        case 'PageUp':
        case 'PageDown':
          logger.warn('[BlinqImageEditor] Focused canvas preventing key', { key: ev.key });
          ev.preventDefault(); // prevent the end key doing anything
      }
    }
  };

  // add handlers to deal with pinch to zoom
  useEffect(() => {
    const downHandler = (ev: PointerEvent) => pointerDownHandler(ev, pointerState.current);
    const upHandler = (ev: PointerEvent) => pointerUpHandler(ev, pointerState.current);
    const current = canvasRef.current; // get a local ref
    current?.addEventListener('pointerdown', downHandler);
    current?.addEventListener('pointermove', moveHandler);

    current?.addEventListener('pointerup', upHandler);
    current?.addEventListener('pointercancel', upHandler);
    current?.addEventListener('pointerout', upHandler);
    current?.addEventListener('pointerleave', upHandler);

    return () => {
      current?.removeEventListener('pointerdown', downHandler);
      current?.removeEventListener('pointermove', moveHandler);
      current?.removeEventListener('pointerup', upHandler);
      current?.removeEventListener('pointercancel', upHandler);
      current?.removeEventListener('pointerout', upHandler);
      current?.removeEventListener('pointerleave', upHandler);
    };
  }, [pointerState, moveHandler]);

  useEffect(() => {
    const current = canvasRef.current;
    const doubleClickHandler = () => {
      zoomSingleton.current.zoom = zoomSingleton.current.default;
      if (centerpointRef.current) {
        positionSetterRef.current = { ...centerpointRef.current };
      }
      setZoom(zoomSingleton.current.default);
    };
    current?.addEventListener('dblclick', doubleClickHandler);
    return () => current?.removeEventListener('dblclick', doubleClickHandler);
  }, [setZoom]);

  function handleDoubleTap() {
    if (!canvasRef.current) {
      return;
    }

    setZoom(zoomSingleton.current.default);
  }

  function handleTouchStart() {
    const currentTime = Date.now();

    if (
      touchStartTimeRef.current &&
      currentTime - touchStartTimeRef.current < DOUBLE_TAP_THRESHOLD
    ) {
      handleDoubleTap();
    } else {
      touchStartTimeRef.current = currentTime;
    }
  }

  return (
    <>
      <div className={styles.editor}>
        <div className={styles['canvas-and-message-container']}>
          <div className={styles['canvas-container']} ref={canvasContainerRef}>
            <canvas
              tabIndex={0}
              style={{ height: canvasHeight }}
              ref={canvasRef}
              onKeyDown={handleCanvasKeyDown}
              onTouchStart={handleTouchStart}
            ></canvas>
          </div>
          <div className={styles['message-container']}>
            <span data-variant={isSettingBackgroundColor || canReposition ? 'shown' : 'hidden'}>
              {isSettingBackgroundColor && !(disableBgEdit || imgCover)
                ? editorState.showCackgroundColorChangingMessage
                  ? 'Background color updated'
                  : null
                : 'Drag to reposition'}
            </span>
          </div>
        </div>
        {editorState.zoomBounds && (
          <ZoomControls
            zoom={editorState.zoom || 1}
            setZoom={setZoom}
            zoomBounds={editorState.zoomBounds}
            defaultZoom={editorState.zoomBounds.default}
          />
        )}
      </div>
    </>
  );
};

/** Image editor with color suggestions.
 * You should probably import ./EditorSuspended.tsx
 */
const EditorWithColorSuggestion: React.FC<React.PropsWithChildren<ImageEditorProps>> = (p) => {
  const suggestion = useColorSuggestions(p.suggestColors ? p.imgSrc : null);
  const { updateEditorState } = p;

  useOnce(
    (preventSecondRun) => {
      if (suggestion.colors) {
        preventSecondRun();
        updateEditorState({ type: 'COLOR_SUGGESTIONS', colors: suggestion.colors });
        // only update if undefined
        if (p.editorState.backgroundColor === null || p.editorState.backgroundColor === undefined) {
          updateEditorState({
            type: 'SET_BG_COLOR',
            color: suggestion.colors[0],
            showMessage: false,
          });
        }
      }
    },
    [suggestion, updateEditorState, p.editorState.backgroundColor, p.suggestColors],
  );

  if (!suggestion.loading || !p.suggestColors) {
    return <ImageEditor {...p} />;
  } else {
    return <ImageEditorLoader />;
  }
};

export default EditorWithColorSuggestion;
